import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { faHistory } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, useState } from 'react';

import { StyledAuditHistoryButton } from '~/common/components/AuditHistoryButton';
import Button from '~/common/components/Button';
import Flex from '~/common/components/Flex';
import { useAuditHistoryWindow } from '~/common/hooks/useAuditHistoryWindow';
import { getCategoryNumber } from '~/pages/AuditHistory/constants';
import theme from '~/theme/';

import IconBadge from '../../mui/Badge/IconBadge';
import FilterMenu from './Filters/FilterMenu';
import { StyledIconButton, StyledLink } from './GridStyledComponents';

type CrudGridToolbarProps = {
  filters?: any[];
  appliedFilters?: any[];
  onFilterChange?: (...args: any[]) => any;
  allowFilters?: boolean;
  multiSelectAction?: {
    buttonLabel?: string;
    subActions?: any[];
  };
  onActionClick?: (...args: any[]) => any;
  isReadOnly?: boolean;
  isMultiSelectActionMode?: boolean;
  onSubActionClick?: (...args: any[]) => any;
  isAuditHistoryEnabled?: boolean;
};

export default function CrudGridToolbar({
  filters,
  appliedFilters,
  onFilterChange,
  allowFilters,
  multiSelectAction,
  onActionClick,
  isReadOnly,
  isMultiSelectActionMode,
  onSubActionClick,
  isAuditHistoryEnabled,
}: Readonly<CrudGridToolbarProps>) {
  const [_, category] = globalThis.location.pathname.split('summary/');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const auditHistoryPath = `/summary/${category || ''}`;
  const { handleChangePageId } = useAuditHistoryWindow(auditHistoryPath);

  const totalFilters = useMemo(() => {
    return appliedFilters.reduce((acc, filter) => {
      return acc + filter.value.length;
    }, 0);
  }, [appliedFilters]);

  const heightNeededMultipleDeleteOpen = '93px';
  const heightNeededFilterOpen = '58px';

  return (
    <Flex
      id="crudGridToolbar"
      justifyContent={'flex-end'}
      direction={'column'}
      width={'100%'}
      height={
        isMultiSelectActionMode
          ? heightNeededMultipleDeleteOpen
          : heightNeededFilterOpen
      }
      marginBottom={'3px'}
    >
      <Flex
        id="crud-grid-main-toolbar"
        alignItems={'flex-end'}
        justifyContent={'flex-end'}
      >
        {isFilterOpen && (
          <FilterMenu
            filters={filters}
            currentFilters={appliedFilters}
            onFilterChange={onFilterChange}
          />
        )}
        <Flex
          id="crudGridToolbarBelt"
          justifyContent={'flex-end'}
          alignItems={'flex-end'}
          mb={multiSelectAction ? 'unset' : '0.5rem'}
        >
          {allowFilters && (
            <IconBadge
              badgeContent={totalFilters}
              color={theme.colors?.teal[50]}
            >
              <StyledIconButton
                variant="outline"
                aria-label="Filter"
                colorScheme={'blue'}
                marginLeft={'0.5em'}
                data-testid={'crud-grid-filter-button'}
                icon={<FontAwesomeIcon icon={faFilter} />}
                active={isFilterOpen}
                onClick={() => {
                  setIsFilterOpen((isOpen) => !isOpen);
                }}
              />
            </IconBadge>
          )}

          {multiSelectAction && !isReadOnly && (
            <Button
              isPrimary={false}
              onClick={() => {
                onActionClick();
              }}
              marginLeft={'0.75rem'}
              data-testid={'delete-multiple'}
            >
              {multiSelectAction.buttonLabel}
            </Button>
          )}
          {isAuditHistoryEnabled && !isReadOnly && (
            <StyledAuditHistoryButton
              rightIcon={<FontAwesomeIcon icon={faHistory} />}
              marginLeft={'0.75rem'}
              data-testid="audit-history--button"
              aria-label={`${category} Audit History`}
              isPrimary={false}
              onClick={() => {
                handleChangePageId(getCategoryNumber(category));
              }}
            />
          )}
        </Flex>
      </Flex>
      <Flex
        direction={'row-reverse'}
        justifyContent="flex-start"
        marginTop={'0.5rem'}
      >
        {multiSelectAction?.subActions?.map((action) => {
          const id = action.id ?? 'selectDeselectAllButton';

          return (
            <StyledLink
              key={id}
              id={id}
              data-testId={id}
              onClick={() => {
                onSubActionClick(action);
              }}
              mb={isMultiSelectActionMode ? '1.5rem' : '0.5rem'}
              ml={'1rem'}
              height={isMultiSelectActionMode ? 'auto' : 0}
              overflow={'hidden'}
              fontSize={'0.8125rem'}
              fontWeight={'500'}
              placeItems={'flex-end'}
              color={'blue.600'}
              minWidth={'unset'}
              transition={'unset'}
            >
              {action.label}
            </StyledLink>
          );
        })}
      </Flex>
    </Flex>
  );
}
